import { useEffect, useRef } from 'react';
import { getAIHandoffs } from '../services/api';
import { AIHandoff } from '../types';

interface AIAlertPollingOptions {
  enabled: boolean;
  onUpdate: (alerts: AIHandoff[]) => void;
  onIncrease?: (alerts: AIHandoff[]) => void;
  visibleMs?: number;
  hiddenMs?: number;
}

export const useAIAlertPolling = ({
  enabled,
  onUpdate,
  onIncrease,
  visibleMs = 5000,
  hiddenMs = 30_000,
}: AIAlertPollingOptions) => {
  const onUpdateRef = useRef(onUpdate);
  const onIncreaseRef = useRef(onIncrease);
  const previousIdsRef = useRef<Set<number> | null>(null);

  onUpdateRef.current = onUpdate;
  onIncreaseRef.current = onIncrease;

  useEffect(() => {
    if (!enabled) {
      previousIdsRef.current = null;
      return undefined;
    }

    let cancelled = false;
    let polling = false;
    let timer: number | undefined;
    let currentDelay =
      document.visibilityState === 'hidden' ? hiddenMs : visibleMs;

    const clearTimer = () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timer = undefined;
      }
    };

    const schedule = (delay: number) => {
      clearTimer();
      timer = window.setTimeout(() => {
        timer = undefined;
        void poll();
      }, delay);
    };

    const poll = async () => {
      if (cancelled || polling) return;
      polling = true;
      try {
        const alerts = await getAIHandoffs('pending');
        if (cancelled) return;

        const previousIds = previousIdsRef.current;
        onUpdateRef.current(alerts);
        if (previousIds !== null) {
          const added = alerts.filter((entry) => !previousIds.has(entry.id));
          if (added.length > 0) onIncreaseRef.current?.(added);
        }
        previousIdsRef.current = new Set(alerts.map((entry) => entry.id));
        currentDelay =
          document.visibilityState === 'hidden' ? hiddenMs : visibleMs;
        schedule(currentDelay);
      } catch {
        if (cancelled) return;
        currentDelay = Math.min(
          Math.max(currentDelay * 2, visibleMs),
          60_000,
        );
        schedule(currentDelay);
      } finally {
        polling = false;
      }
    };

    const handleVisibilityChange = () => {
      if (cancelled) return;
      clearTimer();
      currentDelay =
        document.visibilityState === 'hidden' ? hiddenMs : visibleMs;
      if (document.visibilityState === 'visible') {
        void poll();
      } else if (!polling) {
        schedule(hiddenMs);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    void poll();

    return () => {
      cancelled = true;
      clearTimer();
      previousIdsRef.current = null;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, hiddenMs, visibleMs]);
};
